import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { isSeriesAltitude } from '../components/Layout/sidebarMap';
import { isSeriesRoomPath } from '../utils/seriesBookBoard';
import { plottrackPath } from '../utils/plottrackPath';

/**
 * Single shared entry point for switching the active project/story from
 * outside the Story Board or Chat routes (the global LeftNav switcher,
 * NavigationTree's focus handlers). Board and Chat already navigate on
 * their own when a story changes; this hook exists so every OTHER caller
 * keeps the URL and AppContext in sync instead of writing to context
 * alone, which is what let the two disagree (F9 plan §2-3).
 *
 * Scope note: /plottrack/:storyId encodes story identity in its URL
 * (legacy /board/:storyId redirects there). Book Notes, Bookmarks,
 * Search, and the docked reference panels have no story-identifying
 * URL segment to keep in sync — see f9-plan.md §5.
 */
export function useStoryNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setProjectAndStory,
    setCurrentProject,
    currentStory,
    currentSession,
  } = useApp();

  const switchStory = useCallback((project, story) => {
    setProjectAndStory(project, story);
    if (location.pathname.startsWith('/plottrack') || location.pathname.startsWith('/board')) {
      navigate(plottrackPath(story.id));
      return;
    }
    // Jump from series altitude (Timeline, Series Notes, Notes, a
    // series sitting) enters the book. Book rooms keep their route
    // and pick up the new story from context.
    const leavingSeries = isSeriesRoomPath(location.pathname)
      || isSeriesAltitude({
        isSeries: Boolean(project?.is_series),
        pathname: location.pathname,
        currentStory,
        currentSession,
      });
    if (location.pathname.startsWith('/scene-writing')) {
      navigate(`/scene-writing/${story.id}`);
      return;
    }
    if (leavingSeries) {
      if (story.discovery_session_id) {
        navigate(`/chat/${story.discovery_session_id}`);
      } else {
        navigate(plottrackPath(story.id));
      }
    }
  }, [navigate, location.pathname, setProjectAndStory, currentStory, currentSession]);

  const switchProject = useCallback((project) => {
    setCurrentProject(project);
    // No story chosen yet — nothing to put in the URL. If the user is on
    // /plottrack, PlotTrackPage's own auto-pick effect picks a story and
    // navigates once projectStories load; this function doesn't need to
    // duplicate that.
  }, [setCurrentProject]);

  return { switchStory, switchProject };
}
